import { computed, ref } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";

export function useHuddleControls() {
  const store = useStore();

  const isMuted = computed(() => store.getters["huddles/isMuted"]);
  const videoEnabled = computed(() => store.getters["huddles/videoEnabled"]);
  const screenSharing = computed(() => store.getters["huddles/screenSharing"]);
  const isLeaving = computed(() => store.getters["huddles/isLeaving"]);
  const participants = computed(() => store.getters["huddles/participants"] || []);
  const currentUserId = computed(() => store.getters["auth/userData"]?.id);

  const isOtherScreenSharing = computed(() => {
    return participants.value.some(
      (p) => p.screen_sharing && String(p.user_id) !== String(currentUserId.value)
    );
  });

  const isScreenShareDisabled = computed(() => isOtherScreenSharing.value && !screenSharing.value);

  const isTogglingMute = ref(false);
  const isTogglingVideo = ref(false);
  const isTogglingScreenShare = ref(false);

  const showHuddleError = () => {
    const error = store.getters["huddles/error"];
    if (error) message.error(error);
  };

  const handleToggleMute = async () => {
    if (isTogglingMute.value) return;
    isTogglingMute.value = true;
    try {
      await store.dispatch("huddles/toggleMute");
    } catch (error) {
      console.error("useHuddleControls: failed to toggle mute", error);
      showHuddleError();
    } finally {
      isTogglingMute.value = false;
    }
  };

  const handleToggleVideo = async () => {
    if (isTogglingVideo.value) return;
    isTogglingVideo.value = true;
    try {
      await store.dispatch("huddles/toggleVideo");
    } catch (error) {
      console.error("useHuddleControls: failed to toggle video", error);
      showHuddleError();
    } finally {
      isTogglingVideo.value = false;
    }
  };

  const handleToggleScreenShare = async () => {
    if (isTogglingScreenShare.value) return;
    isTogglingScreenShare.value = true;
    try {
      await store.dispatch("huddles/toggleScreenShare");
    } catch (error) {
      console.error("useHuddleControls: failed to toggle screen share", error);
      showHuddleError();
    } finally {
      isTogglingScreenShare.value = false;
    }
  };

  const handleLeave = async () => {
    try {
      await store.dispatch("huddles/leaveHuddle");
    } catch (error) {
      console.error("useHuddleControls: failed to leave huddle", error);
    }
  };

  return {
    isMuted,
    videoEnabled,
    screenSharing,
    isLeaving,
    isScreenShareDisabled,
    isTogglingMute,
    isTogglingVideo,
    isTogglingScreenShare,
    handleToggleMute,
    handleToggleVideo,
    handleToggleScreenShare,
    handleLeave,
  };
}
